import { type ReactNode } from 'react';
import { Box, Card, CardContent, Divider, Typography } from '@mui/material';
import { DateField, Show, useRecordContext } from 'react-admin';
import { RequireAccess } from '../auth/RequireAccess';
import type { Event } from '../dataProvider';

/**
 * The event detail (Show) for the events explorer (D-events-explorer, Phase 9). This is
 * where the surface EXTENDS the AuditList template: the audit trail's compact one-line diff
 * is not enough for an event, so this renders the full JSONB property bag PRETTY-PRINTED.
 * The top-level fields (user, type, occurred, market) plus the whole properties object.
 * READ-ONLY: there is no action here, an event is an immutable behavioural record.
 */

const Field = ({ label, children }: { label: string; children: ReactNode }) => (
  <Box sx={{ mb: 1.5 }}>
    <Typography variant="caption" color="text.secondary" component="div">
      {label}
    </Typography>
    <Typography variant="body2" component="div">
      {children}
    </Typography>
  </Box>
);

const EventShowLayout = () => {
  const record = useRecordContext<Event>();
  if (!record) return null;

  return (
    <Box sx={{ maxWidth: 760 }} data-testid="event-detail">
      <Card>
        <CardContent>
          <Typography variant="overline" color="text.secondary">
            Event
          </Typography>
          <Typography
            variant="h6"
            component="h1"
            sx={{ mb: 2, fontFamily: 'monospace' }}
          >
            {record.eventType}
          </Typography>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
              gap: 1,
            }}
          >
            <Field label="User id">{String(record.userId)}</Field>
            <Field label="Event type">{record.eventType}</Field>
            <Field label="Occurred">
              <DateField source="occurredAt" showTime />
            </Field>
            <Field label="Market">{String(record.market).toUpperCase()}</Field>
          </Box>

          <Divider sx={{ my: 2 }} />

          {/* The full JSONB property bag, pretty-printed (the extension over the audit
              template's one-line diff). */}
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Properties
          </Typography>
          <Box
            component="pre"
            data-testid="event-properties"
            sx={{
              m: 0,
              p: 2,
              borderRadius: 1,
              bgcolor: 'action.hover',
              fontFamily: 'monospace',
              fontSize: '0.8rem',
              overflowX: 'auto',
              whiteSpace: 'pre-wrap',
            }}
          >
            {JSON.stringify(record.properties ?? {}, null, 2)}
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

/** The detail view. Exported for testing without the access wrapper. */
export const EventShowView = () => (
  <Show title="Event" actions={false} resource="events">
    <EventShowLayout />
  </Show>
);

/** Gated entry point registered as the events Resource's show. */
export const EventShow = () => (
  <RequireAccess roles={['support', 'super_admin', 'fraud']}>
    <EventShowView />
  </RequireAccess>
);
